import { useState, useCallback } from 'react';
import { Zap, Loader2, Copy, Check } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const AI_TARGETS = ['chatgpt', 'claude', 'gemini', 'perplexity', 'copilot'];

export default function TokenOptimizerPage() {
  const [text, setText] = useState('');
  const [targetAi, setTargetAi] = useState('chatgpt');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [copied, setCopied] = useState(false);

  const handleOptimize = useCallback(async () => {
    if (text.trim().length < 10 || loading) return;
    setLoading(true);
    setResult(null);
    try {
      const res = await fetch(`${API}/api/ai/token-optimize`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ text, target_ai: targetAi }),
      });
      if (!res.ok) { const e = await res.json().catch(() => ({})); throw new Error(e.detail || 'Failed'); }
      setResult(await res.json());
    } catch (err) { toast.error(err.message); }
    finally { setLoading(false); }
  }, [text, targetAi, loading]);

  const handleCopy = () => {
    if (result?.optimized) {
      navigator.clipboard.writeText(result.optimized);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const savePct = result?.savings_pct || 0;

  return (
    <div className="space-y-6 max-w-4xl mx-auto" data-testid="optimizer-page">
      <div>
        <h1 className="font-space text-2xl md:text-3xl text-white mb-2">Token Optimizer</h1>
        <p className="text-white/40 text-sm">Reduce token count 40&ndash;70% while preserving output quality</p>
      </div>

      {/* How to use */}
      <div className="bg-[#0E1830] border border-white/5 rounded-xl p-4">
        <details>
          <summary className="text-white/50 text-xs font-bold uppercase tracking-wider cursor-pointer hover:text-white/70">How to use Token Optimizer</summary>
          <div className="mt-3 text-white/35 text-xs space-y-1 leading-relaxed">
            <p>1. Paste any AI cast/instruction into the text area</p>
            <p>2. Select the target AI platform for model-specific optimization</p>
            <p>3. Click &ldquo;Optimize Tokens&rdquo; to reduce token count</p>
            <p>4. See live before/after comparison with cost savings</p>
            <p>5. Copy the optimized version and use it in your AI platform</p>
          </div>
        </details>
      </div>

      {/* Sample */}
      <div className="flex flex-wrap gap-2">
        <span className="text-white/20 text-xs self-center">Try:</span>
        {[
          { label: 'Verbose prompt', text: 'Act as an extremely experienced and highly knowledgeable senior software engineer with more than 20 years of extensive professional experience in the field of web development. I need you to carefully and thoroughly write a very comprehensive and detailed code review of the following React component, making sure to cover all aspects including performance, readability, maintainability, best practices, and potential bugs.' },
          { label: 'Long instruction', text: 'You are a marketing expert. Please help me create a detailed marketing strategy document that covers social media marketing, content marketing, email marketing, SEO optimization, and paid advertising. Make sure to include specific metrics, KPIs, timelines, and budget allocations for each channel. Also provide competitor analysis and market research findings.' },
        ].map(s => (
          <button key={s.label} onClick={() => { setText(s.text); setResult(null); }}
            className="px-3 py-1 text-xs rounded-full border border-white/10 text-white/40 hover:text-white/70 hover:border-white/20 transition-all">{s.label}</button>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        {/* Input */}
        <div className="bg-[#0E1830] border border-white/5 rounded-2xl p-6 space-y-4">
          <textarea value={text} onChange={e => setText(e.target.value)}
            placeholder="Paste your AI cast/instruction here..."
            className="w-full h-40 bg-white/[0.04] border border-white/[0.08] rounded-xl p-4 text-white/90 text-sm resize-none focus:border-[#7C3AED]/40 focus:outline-none placeholder:text-white/20"
            data-testid="optimizer-input" />
          <div className="space-y-2">
            <span className="text-[10px] uppercase tracking-widest text-white/30 font-bold">Target AI</span>
            <div className="flex flex-wrap gap-1.5">
              {AI_TARGETS.map(ai => (
                <button key={ai} onClick={() => setTargetAi(ai)}
                  className={`px-3 py-1.5 text-xs rounded-full border font-medium transition-all capitalize ${targetAi === ai ? 'bg-[#7C3AED] text-white border-[#7C3AED]' : 'bg-white/[0.03] text-white/40 border-white/[0.08] hover:text-white/60'}`}
                  data-testid={`target-${ai}`}>{ai}</button>
              ))}
            </div>
          </div>
          <Button onClick={handleOptimize} disabled={loading || text.trim().length < 10}
            className="w-full shimmer-btn text-white rounded-full font-space font-bold border-0" data-testid="optimize-btn">
            {loading ? <><Loader2 className="w-4 h-4 animate-spin mr-2" /> Optimizing...</> : <><Zap className="w-4 h-4 mr-2" /> Optimize Tokens</>}
          </Button>
        </div>

        {/* Result */}
        <div className="bg-[#0E1830] border border-white/5 rounded-2xl p-6">
          {result ? (
            <div className="space-y-4" data-testid="optimizer-result">
              {/* Token Meter */}
              <div className="bg-white/[0.04] border border-white/[0.07] rounded-xl p-4 space-y-3">
                <div className="flex items-center justify-between">
                  <span className="text-white/30 text-xs font-bold uppercase tracking-wider">Token Savings</span>
                  <span className="text-xs font-bold text-emerald-400 bg-emerald-500/10 rounded-full px-2.5 py-0.5" data-testid="savings-badge">Save {savePct}%</span>
                </div>
                <div className="space-y-2">
                  <div className="flex items-center gap-2">
                    <span className="text-white/30 text-xs w-12">Before</span>
                    <div className="flex-1 h-2 bg-white/[0.06] rounded-full overflow-hidden">
                      <div className="h-full rounded-full bg-red-500/50" style={{ width: '100%' }} />
                    </div>
                    <span className="text-white text-xs w-20 text-right">{result.tokens_before} tokens</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-white/30 text-xs w-12">After</span>
                    <div className="flex-1 h-2 bg-white/[0.06] rounded-full overflow-hidden">
                      <div className="h-full rounded-full bg-gradient-to-r from-emerald-500 to-cyan-400" style={{ width: `${Math.max(5, 100 - savePct)}%` }} />
                    </div>
                    <span className="text-emerald-400 text-xs w-20 text-right">{result.tokens_after} tokens</span>
                  </div>
                </div>
                <div className="flex items-center gap-2 text-xs pt-1 border-t border-white/[0.06]">
                  <span className="line-through text-red-400/50">{result.cost_before}</span>
                  <span className="text-white/20">&rarr;</span>
                  <span className="text-emerald-400 font-bold">{result.cost_after}</span>
                </div>
              </div>

              {/* Optimized Cast */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-xs font-bold uppercase text-[#06B6D4] tracking-wider">Optimized Cast</span>
                  <button onClick={handleCopy} className="text-xs text-[#7C3AED] hover:underline flex items-center gap-1" data-testid="copy-btn">
                    {copied ? <><Check className="w-3 h-3" /> Copied</> : <><Copy className="w-3 h-3" /> Copy</>}
                  </button>
                </div>
                <div className="bg-white/[0.03] border border-white/[0.06] rounded-xl p-4 max-h-[250px] overflow-y-auto">
                  <p className="text-white/80 text-sm leading-relaxed whitespace-pre-wrap" data-testid="optimized-text">{result.optimized}</p>
                </div>
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center h-full text-center py-12">
              <Zap className="w-10 h-10 text-white/10 mb-3" />
              <p className="text-white/30 text-sm">Paste a cast and click Optimize</p>
              <p className="text-white/15 text-xs mt-1">Same quality, fewer tokens, lower cost</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
